import { useState, useEffect, useRef, useCallback } from 'react';
import { fiscalYearsApi } from '../services/api/endpoints/fiscalYears';
import { usersApi } from '../services/api/endpoints/users';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useAuthStore } from '../stores/authStore';
import { FallbackService } from '../services/FallbackService';
import type { FiscalYear } from '../types/fiscal-year';

export function useFiscalYear() {
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false); // Prevent multiple concurrent calls
  const initializedFromBackendRef = useRef(false); // Track if we loaded from backend preferences
  const lastOrgIdRef = useRef<string | null>(null); // Track org changes
  
  const { currentFiscalYear, setCurrentFiscalYear, isSyncingToBackend } = useFiscalYearStore();
  const organizationId = useAuthStore((s) => s.organization?.id ?? null);

  const loadFiscalYears = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // 1. Charger les exercices fiscaux
      const response = await fiscalYearsApi.getAll();
      if (response.success && response.data) {
        // Gérer le cas où la réponse est enveloppée dans { data: [...] }
        const rawData = response.data;
        const yearsData = Array.isArray(rawData) ? rawData : (rawData.data || []);
        const years = Array.isArray(yearsData) ? yearsData : [];
        setFiscalYears(years);
        
        // 2. Récupérer les préférences utilisateur pour l'exercice actif (première fois seulement)
        let preferredFiscalYearId: string | null = null;
        if (!initializedFromBackendRef.current && years.length > 0) {
          try {
            const prefsResponse = await usersApi.getUserPreferences();
            if (prefsResponse.success && prefsResponse.data?.currentFiscalYearId) {
              preferredFiscalYearId = prefsResponse.data.currentFiscalYearId;
              console.log('📥 Préférence exercice fiscal récupérée du backend:', preferredFiscalYearId);
            }
          } catch (prefsError) {
            console.warn('⚠️ Impossible de récupérer les préférences utilisateur:', prefsError);
          }
          initializedFromBackendRef.current = true;
        }
        
        // 3. Définir l'exercice courant selon la priorité:
        //    a) Préférence backend si valide
        //    b) Exercice local existant si valide
        //    c) Exercice ouvert
        //    d) Premier exercice
        if (!currentFiscalYear || !years.find(y => y.id === currentFiscalYear.id)) {
          // Chercher l'exercice préféré du backend
          const preferredYear = preferredFiscalYearId 
            ? years.find(y => y.id === preferredFiscalYearId) 
            : null;
          const openYear = years.find(year => year.status === 'open');
          
          // skipBackendSync=true car on vient de le charger depuis le backend
          const fiscalYearToSet = preferredYear || openYear || years[0] || null;
          setCurrentFiscalYear(fiscalYearToSet, preferredYear ? true : false);
        }
      } else {
        // Fallback vers les données mock si l'API échoue
        console.warn('⚠️ API fiscal years failed, trying fallback...');
        const fallbackResponse = await FallbackService.getFallbackData<FiscalYear[]>('/fiscal-years');
        
        if (fallbackResponse?.success && fallbackResponse.data) {
          const fallbackYears = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
          console.log('✅ Using fallback fiscal years:', fallbackYears.length);
          setFiscalYears(fallbackYears);
          
          const openYear = fallbackYears.find(year => year.status === 'open');
          if (!currentFiscalYear) {
            setCurrentFiscalYear(openYear || fallbackYears[0] || null);
          }
        } else {
          setError(response.error || 'Erreur lors du chargement des exercices fiscaux');
          setFiscalYears([]);
        }
      }
    } catch (err) {
      console.error('Failed to load fiscal years:', err);
      
      // Fallback en cas d'erreur réseau
      try {
        console.warn('⚠️ Network error, trying fallback...');
        const fallbackResponse = await FallbackService.getFallbackData<FiscalYear[]>('/fiscal-years');
        
        if (fallbackResponse?.success && fallbackResponse.data) {
          const fallbackYears = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
          console.log('✅ Using fallback fiscal years after error:', fallbackYears.length);
          setFiscalYears(fallbackYears);
          
          const openYear = fallbackYears.find(year => year.status === 'open');
          if (!currentFiscalYear) {
            setCurrentFiscalYear(openYear || fallbackYears[0] || null);
          }
          // Clear error since fallback worked
          setError(null);
        } else {
          setError('Impossible de charger les exercices fiscaux');
          setFiscalYears([]);
        }
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
        setError('Impossible de charger les exercices fiscaux');
        setFiscalYears([]);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [currentFiscalYear, setCurrentFiscalYear]);

  // Re-fetch fiscal years on mount AND when organization changes (auditor company switch)
  useEffect(() => {
    if (organizationId && organizationId !== lastOrgIdRef.current) {
      // Organization changed: reset state to force fresh load
      if (lastOrgIdRef.current !== null) {
        console.log('🔄 [FiscalYear] Organization changed:', lastOrgIdRef.current, '→', organizationId);
        initializedFromBackendRef.current = false;
        loadingRef.current = false;
        setFiscalYears([]);
        setCurrentFiscalYear(null, true); // skipBackendSync
      }
      lastOrgIdRef.current = organizationId;
    }
    loadFiscalYears();
  }, [organizationId]);

  const createFiscalYear = async (data: { startDate: string; endDate: string; code: string }): Promise<FiscalYear | null> => {
    try {
      setLoading(true);
      const response = await fiscalYearsApi.create(data);
      if (response.success && response.data) {
        // Gérer le cas où la réponse est enveloppée dans { data: {...} }
        const fiscalYearData = response.data.data || response.data;
        // Recharger la liste des exercices fiscaux
        await loadFiscalYears();
        // Définir le nouvel exercice comme exercice courant
        setCurrentFiscalYear(fiscalYearData);
        return fiscalYearData;
      } else {
        setError(response.error || 'Erreur lors de la création de l\'exercice fiscal');
        return null;
      }
    } catch (err) {
      console.error('Failed to create fiscal year:', err);
      setError('Impossible de créer l\'exercice fiscal');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (fiscalYear: FiscalYear) => {
    const start = new Date(fiscalYear.startDate);
    const end = new Date(fiscalYear.endDate);
    return `${start.toLocaleDateString('fr-FR')} - ${end.toLocaleDateString('fr-FR')}`;
  };

  const reopenFiscalYear = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      const response = await fiscalYearsApi.reopen(id);
      if (response.success && response.data) {
        // Gérer le cas où la réponse est enveloppée dans { data: {...} }
        const fiscalYearData = response.data.data || response.data;
        setCurrentFiscalYear(fiscalYearData);
        // Recharger la liste pour avoir les statuts à jour
        await loadFiscalYears();
      } else {
        setError(response.error || 'Erreur lors de la réouverture de l\'exercice fiscal');
        throw new Error(response.error || 'Erreur lors de la réouverture');
      }
    } catch (err) {
      console.error('Failed to reopen fiscal year:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const closeFiscalYear = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      const response = await fiscalYearsApi.close(id);
      if (response.success && response.data) {
        // Recharger la liste pour avoir les statuts à jour
        await loadFiscalYears();
      } else {
        setError(response.error || 'Erreur lors de la clôture de l\'exercice fiscal');
        throw new Error(response.error || 'Erreur lors de la clôture');
      }
    } catch (err) {
      console.error('Failed to close fiscal year:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectFiscalYear = (fiscalYear: FiscalYear) => {
    setCurrentFiscalYear(fiscalYear);
  };

  return {
    fiscalYears,
    currentFiscalYear,
    loading,
    error,
    isSyncingToBackend, // Indique si la préférence est en cours de sync vers le serveur
    formatPeriod,
    createFiscalYear,
    reopenFiscalYear,
    closeFiscalYear,
    selectFiscalYear,
    loadFiscalYears
  };
}